import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Buildings, ChartLineUp, Key,
  MapPin, Quotes, ShieldCheck, Lightning, MagnifyingGlass, Bank,
} from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import PropertyCard from "@/components/PropertyCard";
import SiteNav from "@/components/SiteNav";
import Seo, { agentJsonLd } from "@/lib/seo";
import { AGENT_PHOTO, BRAND, LENDER, TESTIMONIALS } from "@/lib/constants";

const HERO = "https://images.pexels.com/photos/24589250/pexels-photo-24589250.jpeg";
const INTERIOR = "https://images.pexels.com/photos/31666687/pexels-photo-31666687.jpeg";
const PORTRAIT = AGENT_PHOTO;

function Marquee() {
  const items = ["NEW YORK STATE", "LICENSED SALESPERSON", "BUYERS", "SELLERS", "BROOKLYN", "MANHATTAN", "QUEENS", "WHITE-GLOVE SERVICE"];
  return (
    <div className="border-y border-white/5 bg-[#101116] overflow-hidden py-4">
      <div className="flex gap-12 whitespace-nowrap animate-[marquee_28s_linear_infinite]">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="text-white/40 text-sm tracking-[0.25em] flex items-center gap-12">
            {t} <span className="text-brand">✦</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0);} to { transform: translateX(-50%);} }`}</style>
    </div>
  );
}

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  useEffect(() => {
    api.get("/properties/featured", { params: { limit: 6 } })
      .then(({ data }) => setFeatured((Array.isArray(data) ? data : []).slice(0, 3)))
      .catch(() => {
        // Fallback if featured route isn't deployed yet.
        api.get("/properties", { params: { status: "active", limit: 6, sort: "newest" } })
          .then(({ data }) => setFeatured((Array.isArray(data) ? data : []).slice(0, 3)))
          .catch(() => {});
      });
    api.get("/site-settings")
      .then(({ data }) => {
        const fromDb = Array.isArray(data?.testimonials) ? data.testimonials : [];
        setTestimonials(fromDb.length ? fromDb : TESTIMONIALS);
      })
      .catch(() => {});
  }, []);
  return (
    <div className="bg-background min-h-screen overflow-x-hidden">
      <Seo
        title={null}
        description="The Whyte Glove — luxury real estate in New York with Lamar A. Whyte. Browse listings, search the full MLS, and get white-glove buyer & seller service."
        path="/"
        jsonLd={agentJsonLd()}
        jsonLdId="agent"
      />
      <SiteNav showAbout transparent />

      {/* HERO */}
      <header className="relative min-h-screen flex items-end">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
              <MapPin size={16} weight="duotone" /> {BRAND.agentName} · {BRAND.title} · New York
            </p>
            <h1 className="text-5xl md:text-7xl text-white leading-[1.05] tracking-tight">
              Real estate,<br />
              <span className="italic text-brand">white-glove service.</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 font-light max-w-xl leading-relaxed">
              Buying, selling, or renting — I handle every detail with white-glove care:
              effortless, strategic, and personal. No pressure. Just results.
            </p>
            {/* Hero budget: exactly two primary CTAs; everything else is a quiet text row */}
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link
                to="/mls-search"
                data-testid="hero-mls-btn"
                className="group flex items-center justify-center gap-2 bg-brand text-black px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-all"
              >
                <MagnifyingGlass size={16} weight="bold" /> Search the MLS
              </Link>
              <Link
                to="/home-value"
                data-testid="hero-value-btn"
                className="group flex items-center justify-center gap-2 border border-brand/50 text-brand px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand/10 transition-all"
              >
                What's my home worth? <ArrowUpRight size={16} weight="bold" className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/60">
              <Link to="/buy" data-testid="hero-buy-link" className="hover:text-brand transition-colors">Buying</Link>
              <span className="text-white/20">·</span>
              <Link to="/sell" data-testid="hero-sell-link" className="hover:text-brand transition-colors">Selling</Link>
              <span className="text-white/20">·</span>
              <Link to="/listings?listing_type=rent" data-testid="hero-rent-link" className="hover:text-brand transition-colors">Renting</Link>
              <span className="text-white/20">·</span>
              <Link to="/financing" data-testid="hero-financing-link" className="hover:text-brand transition-colors">Financing</Link>
              <span className="text-white/20">·</span>
              <Link to="/neighborhoods" data-testid="hero-hoods-link" className="hover:text-brand transition-colors">Neighborhoods</Link>
            </div>
          </motion.div>
        </div>
      </header>

      <Marquee />

      {/* VALUE / FEATURES — asymmetric */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5">
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-4">Why work with me</p>
            <h2 className="text-4xl md:text-5xl text-white leading-tight">
              A modern approach to a personal decision.
            </h2>
            <p className="mt-6 text-white/60 font-light leading-relaxed">
              I combine deep local market knowledge with smart technology so you get
              faster answers, sharper negotiation, and a smoother close.
            </p>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Lightning, t: "Instant response", d: "Inquiries reach me in seconds — you're never left waiting." },
              { icon: ChartLineUp, t: "Data-driven pricing", d: "Sharp comps and strategy to maximize your outcome." },
              { icon: ShieldCheck, t: "Honest guidance", d: "Straight talk, no pressure. Your goals lead the way." },
              { icon: Buildings, t: "Full NY coverage", d: "Condos, co-ops, townhouses and multi-families." },
            ].map((f, i) => (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#101116] border border-white/5 rounded-sm p-7 hover:border-brand/40 transition-colors duration-300"
              >
                <f.icon size={28} weight="duotone" className="text-brand mb-4" />
                <h3 className="text-lg text-white mb-2">{f.t}</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED LISTINGS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-28">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-brand text-xs uppercase tracking-[0.3em] mb-4">Available now</p>
              <h2 className="text-4xl md:text-5xl text-white leading-tight">Featured homes</h2>
            </div>
            <Link to="/listings" data-testid="view-all-listings" className="hidden sm:flex items-center gap-2 text-sm text-brand hover:gap-3 transition-all">
              Browse all listings <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
          <div className="sm:hidden mt-8 text-center">
            <Link to="/listings" className="inline-flex items-center gap-2 text-sm text-brand">Browse all listings <ArrowRight size={16} weight="bold" /></Link>
          </div>
        </section>
      )}

      {/* SPLIT CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-sm overflow-hidden min-h-[360px] group">
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: `url(${INTERIOR})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-10">
              <h3 className="text-3xl text-white mb-2">Sell with confidence</h3>
              <p className="text-white/60 font-light mb-6">Find out what your home is worth in 60 seconds.</p>
              <Link to="/sell" data-testid="split-sell-btn"
                className="inline-flex items-center gap-2 text-brand text-sm uppercase tracking-[0.15em] hover:gap-3 transition-all">
                Get my valuation <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="relative rounded-sm overflow-hidden min-h-[360px] group">
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: `url(${HERO})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-10">
              <h3 className="text-3xl text-white mb-2">Buy the right way</h3>
              <p className="text-white/60 font-light mb-6">Get matched to homes that actually fit your life.</p>
              <Link to="/buy" data-testid="split-buy-btn"
                className="inline-flex items-center gap-2 text-brand text-sm uppercase tracking-[0.15em] hover:gap-3 transition-all">
                Start my search <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Rentals + Financing strips */}
      <section className="max-w-7xl mx-auto px-6 pb-28 space-y-6">
        <Link
          to="/listings?listing_type=rent"
          data-testid="landing-rentals"
          className="group flex flex-col md:flex-row md:items-center justify-between gap-6 border border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent p-8 md:p-10 rounded-sm hover:border-brand/40 transition-colors"
        >
          <div className="flex items-start gap-5">
            <span className="h-12 w-12 rounded-sm bg-brand/15 flex items-center justify-center shrink-0">
              <Key size={24} weight="duotone" className="text-brand" />
            </span>
            <div>
              <p className="text-brand text-xs uppercase tracking-[0.3em] mb-2">Rentals</p>
              <h3 className="text-2xl md:text-3xl text-white">Looking to rent in NY?</h3>
              <p className="text-white/50 mt-2 max-w-xl font-light">
                Apartments and homes across the boroughs and Westchester — tour-ready rentals with the same white-glove care.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-brand text-sm uppercase tracking-[0.12em] font-medium group-hover:gap-3 transition-all shrink-0">
            Browse rentals <ArrowRight size={16} weight="bold" />
          </span>
        </Link>

        <Link
          to="/financing"
          data-testid="landing-financing"
          className="group flex flex-col md:flex-row md:items-center justify-between gap-6 border border-brand/25 bg-gradient-to-r from-brand/10 to-transparent p-8 md:p-10 rounded-sm hover:border-brand/50 transition-colors"
        >
          <div className="flex items-start gap-5">
            <span className="h-12 w-12 rounded-sm bg-brand/15 flex items-center justify-center shrink-0">
              <Bank size={24} weight="duotone" className="text-brand" />
            </span>
            <div>
              <p className="text-brand text-xs uppercase tracking-[0.3em] mb-2">Financing partner</p>
              <h3 className="text-2xl md:text-3xl text-white">Need a mortgage? Meet {LENDER.firstName}.</h3>
              <p className="text-white/50 mt-2 max-w-xl font-light">
                {LENDER.name} · {LENDER.titleLong} · NMLS #{LENDER.nmls} — pre-approvals that make your offers stronger.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-brand text-sm uppercase tracking-[0.12em] font-medium group-hover:gap-3 transition-all shrink-0">
            Explore financing <ArrowRight size={16} weight="bold" />
          </span>
        </Link>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-6 pb-28">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="relative rounded-sm overflow-hidden aspect-[4/5]">
              <img src={PORTRAIT} alt={BRAND.agentName} className="w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-brand/20" />
            </div>
          </div>
          <div className="md:col-span-8">
            <Quotes size={40} weight="fill" className="text-brand/40 mb-4" />
            <p className="text-2xl md:text-3xl text-white font-serif leading-snug">
              "I treat every client like family. My job isn't to close a deal — it's to
              get you the right outcome, on your terms, with zero stress — and the care you deserve."
            </p>
            <p className="mt-6 text-white/50 text-sm uppercase tracking-[0.2em]">
              {BRAND.agentName} — {BRAND.titleLong}, {BRAND.broker}
            </p>
          </div>
        </div>
      </section>

      {/* CLIENT WORDS — renders only when real testimonials exist */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-28" data-testid="testimonials-section">
          <p className="text-brand text-xs uppercase tracking-[0.3em] mb-4 text-center">Client words</p>
          <h2 className="text-3xl md:text-4xl text-white text-center mb-12">What clients say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t, i) => (
              <figure key={i} className="rounded-sm border border-white/5 bg-[#101116] p-7">
                <Quotes size={22} weight="fill" className="text-brand/40 mb-3" />
                <blockquote className="text-white/80 font-light leading-relaxed">{t.quote}</blockquote>
                <figcaption className="mt-4 text-white/40 text-xs uppercase tracking-[0.15em]">
                  {t.name}{t.context ? ` · ${t.context}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <div className="flex items-center gap-2 text-white tracking-[0.2em] text-sm mb-4">
                <Key size={18} weight="duotone" className="text-brand" /> {BRAND.mark}
              </div>
              <p className="text-white/40 text-sm font-light max-w-xs">{BRAND.tagline}</p>
            </div>
            <div className="text-sm text-white/50 space-y-1.5">
              <p className="text-white/70">{BRAND.agentName}</p>
              <p>{BRAND.titleLong} · {BRAND.broker}</p>
              <p>{BRAND.address}</p>
              <p>NY Lic. #{BRAND.license}</p>
              <p>
                <a href={`tel:${BRAND.phoneDirect}`} className="hover:text-brand transition-colors">Direct {BRAND.phoneDirect}</a>
                {" · "}
                <a href={`tel:${BRAND.phoneOffice}`} className="hover:text-brand transition-colors">Office {BRAND.phoneOffice}</a>
              </p>
            </div>
            <div className="flex md:justify-end gap-8 text-sm text-white/50">
              <div className="space-y-2">
                <Link to="/" className="block hover:text-brand transition-colors">Home</Link>
                <Link to="/listings" className="block hover:text-brand transition-colors">Listings</Link>
                <Link to="/mls-search" className="block hover:text-brand transition-colors">MLS Search</Link>
                <Link to="/financing" className="block hover:text-brand transition-colors">Financing</Link>
                <Link to="/buy" className="block hover:text-brand transition-colors">Buy</Link>
                <Link to="/sell" className="block hover:text-brand transition-colors">Sell</Link>
                <Link to="/listings?listing_type=rent" className="block hover:text-brand transition-colors">Rent</Link>
                <Link to="/neighborhoods" className="block hover:text-brand transition-colors">Neighborhoods</Link>
                <Link to="/sold" className="block hover:text-brand transition-colors">Sold</Link>
                <Link to="/login" className="block hover:text-brand transition-colors">Agent Login</Link>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/30 text-xs">© {new Date().getFullYear()} {BRAND.agentName}. All rights reserved.</p>
            <p className="text-white/30 text-xs">Equal Housing Opportunity · Listings via OneKey® MLS · NYC market data via public feeds</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
